/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { EmptyState } from '../../../components/v2';
import ResumoHoras from './ResumoHoras';
import {
  ESTADO_AUSENCIA,
  ESTADO_EXTRA_100,
  ESTADO_VIOLACAO,
  URL_DATE_FORMAT,
  chipColor,
  getRegra,
  labelFeriadoTipo,
} from './constants';

moment.locale('pt-br');

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const PONTOS = [
  { tipo: 'INICIO_JORNADA', label: 'Início' },
  { tipo: 'INICIO_INTERVALO', label: 'Intervalo' },
  { tipo: 'FIM_INTERVALO', label: 'Volta' },
  { tipo: 'FIM_JORNADA', label: 'Fim' },
];

const buildPeriodCells = (inicio, fim) => {
  const start = moment(inicio).startOf('day');
  const end = moment(fim).startOf('day');
  if (!start.isValid() || !end.isValid() || start.isAfter(end, 'day')) return [];
  const cells = [];
  for (let i = 0; i < start.day(); i += 1) cells.push(null);
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, 'day')) {
    cells.push(cursor.clone());
    cursor.add(1, 'day');
  }
  return cells;
};

const periodCrossesYear = (inicio, fim) => moment(inicio).year() !== moment(fim).year();

const formatDayLabel = (day, showYear) => day.format(showYear ? 'DD/MM/YYYY' : 'DD/MM');

const groupByDate = (dias) => {
  const map = {};
  (dias || []).forEach((row) => {
    if (!row.dataKey) return;
    if (!map[row.dataKey]) {
      map[row.dataKey] = { itens: [], regras: {}, estados: [] };
    }
    const bucket = map[row.dataKey];
    bucket.itens.push(row);
    row.estados.forEach((estado) => {
      if (!bucket.estados.includes(estado)) bucket.estados.push(estado);
    });
    row.regras.forEach((regraId) => {
      bucket.regras[regraId] = (bucket.regras[regraId] || 0) + 1;
    });
  });
  return map;
};

const cellSx = (theme, { selected, estados }) => {
  const violacao = estados.includes(ESTADO_VIOLACAO);
  const ausencia = estados.includes(ESTADO_AUSENCIA);
  const extra100 = estados.includes(ESTADO_EXTRA_100);
  return {
    minHeight: 76,
    p: 0.75,
    borderRadius: 1,
    border: '1px solid',
    borderColor: selected ? 'primary.main' : 'divider',
    boxShadow: selected ? `0 0 0 1px ${theme.palette.primary.main}` : 'none',
    cursor: 'pointer',
    bgcolor: violacao
      ? alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.28 : 0.12)
      : 'background.paper',
    ...(ausencia && !violacao && {
      bgcolor: alpha(theme.palette.text.primary, 0.04),
      borderStyle: 'dotted',
    }),
    ...(extra100 && !violacao && {
      bgcolor: alpha('#7b1fa2', theme.palette.mode === 'dark' ? 0.28 : 0.1),
      borderStyle: 'dashed',
      borderColor: selected ? 'primary.main' : '#7b1fa2',
    }),
  };
};

const RegraChips = ({ regras }) => (
  <Stack direction="row" spacing={0.25} useFlexGap sx={{ flexWrap: 'wrap', mt: 0.5 }}>
    {Object.keys(regras || {}).map((regraId) => {
      const regra = getRegra(regraId);
      if (!regra) return null;
      return (
        <Chip
          key={regraId}
          size="small"
          label={`${regra.short} · ${regra.label}`}
          color={chipColor(regraId)}
          variant={regraId === 'SEM_JORNADA' ? 'outlined' : 'filled'}
          sx={{
            height: 'auto',
            maxWidth: '100%',
            '& .MuiChip-label': { whiteSpace: 'normal', fontSize: 10, lineHeight: 1.2, px: 0.75, py: 0.25 },
          }}
        />
      );
    })}
  </Stack>
);

RegraChips.propTypes = {
  regras: PropTypes.object,
};

const PontosDia = ({ pontos }) => {
  const byTipo = {};
  (pontos || []).forEach((ponto) => {
    byTipo[ponto.tipo] = ponto.horario;
  });
  return (
    <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {PONTOS.map((ponto) => (
        <Box key={ponto.tipo}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{ponto.label}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{byTipo[ponto.tipo] || '—'}</Typography>
        </Box>
      ))}
    </Stack>
  );
};

PontosDia.propTypes = {
  pontos: PropTypes.arrayOf(PropTypes.object),
};

const feriadoDoBucket = (bucket) => (bucket?.itens || []).map((item) => item.feriado).find(Boolean) || null;

const rotuloFeriado = (feriado) => {
  if (!feriado) return '';
  const tipo = labelFeriadoTipo(feriado.tipo);
  if (feriado.nome && tipo) return `${feriado.nome} · ${tipo}`;
  return feriado.nome || tipo;
};

const temHorario = (item) => Boolean(
  item?.horasRegistradas
  || item?.horasEsperadas
  || (item?.pontos || []).some((ponto) => ponto.horario),
);

const PeriodGrid = ({ byDate, inicio, fim, selectedKey, onSelect, showTotal }) => {
  const cells = buildPeriodCells(inicio, fim);
  const showYear = periodCrossesYear(inicio, fim);
  if (!cells.length) return null;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.5 }}>
      {WEEKDAYS.map((label, index) => (
        <Typography key={`${label}-${index}`} variant="caption" align="center" color="text.secondary" sx={{ fontWeight: 700 }}>
          {label}
        </Typography>
      ))}
      {cells.map((day, index) => {
        if (!day) return <Box key={`empty-${index}`} />;
        const key = day.format(URL_DATE_FORMAT);
        const bucket = byDate[key];
        const estados = bucket?.estados || [];
        const total = showTotal ? bucket?.itens?.[0]?.horasRegistradas : null;
        const feriado = feriadoDoBucket(bucket);
        return (
          <Box
            key={key}
            onClick={() => onSelect(key)}
            sx={(theme) => cellSx(theme, { selected: selectedKey === key, estados })}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: showYear ? 10 : 12, lineHeight: 1.2 }}>
              {formatDayLabel(day, showYear)}
            </Typography>
            {total && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 11, lineHeight: 1.2 }}>
                {total}
              </Typography>
            )}
            {feriado && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10, lineHeight: 1.15 }}>
                {feriado.nome || labelFeriadoTipo(feriado.tipo)}
              </Typography>
            )}
            {bucket && <RegraChips regras={bucket.regras} />}
          </Box>
        );
      })}
    </Box>
  );
};

PeriodGrid.propTypes = {
  byDate: PropTypes.object.isRequired,
  inicio: PropTypes.number.isRequired,
  fim: PropTypes.number.isRequired,
  selectedKey: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  showTotal: PropTypes.bool,
};

const JornadaButton = ({ item, onOpenJornada, sx }) => {
  if (!item?.jornadaId) return null;
  return (
    <Button
      size="small"
      color="inherit"
      startIcon={<OpenInNewOutlinedIcon />}
      onClick={() => onOpenJornada(item.jornadaId, item.inspetorId)}
      sx={sx}
    >
      Abrir jornada
    </Button>
  );
};

JornadaButton.propTypes = {
  item: PropTypes.object,
  onOpenJornada: PropTypes.func.isRequired,
  sx: PropTypes.object,
};

const DiaDetalhe = ({ item, mode, onOpenJornada, jornadaNoItem, ocultarChips }) => {
  const semMarca = !temHorario(item) && item.regras.length === 0 && item.detalhes.length === 0 && item.estados.length === 0;
  if (semMarca) {
    return <Typography variant="body2" color="text.secondary">Sem marca neste dia.</Typography>;
  }
  return (
    <Box sx={{ position: 'relative', pr: jornadaNoItem && item.jornadaId ? 16 : 0 }}>
      {jornadaNoItem && (
        <JornadaButton
          item={item}
          onOpenJornada={onOpenJornada}
          sx={{ position: 'absolute', top: 0, right: 0 }}
        />
      )}
      {mode === 'geral' && (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.nome || 'Inspetor'}</Typography>
      )}
      {temHorario(item) && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {`${item.horasRegistradas || '—'} registradas · ${item.horasEsperadas || '—'} esperadas`}
        </Typography>
      )}
      {(item.pontos || []).some((ponto) => ponto.horario) && (
        <Box sx={{ mt: 1 }}><PontosDia pontos={item.pontos} /></Box>
      )}
      {!ocultarChips && (
        <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', mt: 0.5 }}>
          {item.regras.map((regraId) => {
            const regra = getRegra(regraId);
            if (!regra) return null;
            return <Chip key={regraId} size="small" label={`${regra.short} ${regra.label}`} color={chipColor(regraId)} />;
          })}
          {item.estados.includes(ESTADO_EXTRA_100) && (
            <Chip size="small" label="100% extra" variant="outlined" />
          )}
        </Stack>
      )}
      {item.detalhes.map((texto) => (
        <Typography key={texto} variant="body2" color="text.secondary">{texto}</Typography>
      ))}
    </Box>
  );
};

DiaDetalhe.propTypes = {
  item: PropTypes.object.isRequired,
  mode: PropTypes.oneOf(['inspetor', 'geral']).isRequired,
  onOpenJornada: PropTypes.func.isRequired,
  jornadaNoItem: PropTypes.bool,
  ocultarChips: PropTypes.bool,
};

const CalendarioJornada = ({
  mode,
  dias,
  inicio,
  fim,
  onOpenJornada,
  resumo,
  loading = false,
  loadError = false,
}) => {
  const [selectedKey, setSelectedKey] = useState(null);
  const byDate = useMemo(() => groupByDate(dias), [dias]);
  const selected = selectedKey ? byDate[selectedKey] : null;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {mode === 'geral'
            ? 'Cada dia mostra as regras de todo mundo no período filtrado.'
            : 'Marcas do inspetor no período filtrado. Domingo ou feriado trabalhado não é violação.'}
        </Typography>
        {mode === 'inspetor' && <Box sx={{ mt: 1 }}><ResumoHoras row={resumo} /></Box>}
      </Box>

      {loading && <LinearProgress />}
      {loadError && (
        <Alert severity="warning">Não foi possível carregar os horários deste inspetor.</Alert>
      )}

      {mode === 'geral' && !dias.length ? (
        <EmptyState
          title="Nenhum dia neste recorte"
          message="O relatório não devolveu dias para montar o calendário."
        />
      ) : (
        <PeriodGrid
          byDate={byDate}
          inicio={inicio}
          fim={fim}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          showTotal={mode === 'inspetor'}
        />
      )}

      {selectedKey && (
        <Box sx={{ position: 'relative', p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {selected?.itens?.length === 1 && (
            <JornadaButton
              item={selected.itens[0]}
              onOpenJornada={onOpenJornada}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            />
          )}
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap', mb: 1, pr: selected?.itens?.length === 1 ? 16 : 0 }}>
            <Typography variant="subtitle2">
              {moment(selectedKey, URL_DATE_FORMAT).format('DD/MM/YYYY — dddd')}
            </Typography>
            {selected?.itens?.length === 1 && selected.itens[0].regras.map((regraId) => {
              const regra = getRegra(regraId);
              if (!regra) return null;
              return <Chip key={regraId} size="small" label={`${regra.short} ${regra.label}`} color={chipColor(regraId)} />;
            })}
            {selected?.itens?.length === 1 && selected.itens[0].estados.includes(ESTADO_EXTRA_100) && (
              <Chip size="small" label="100% extra" variant="outlined" />
            )}
            {selected?.itens?.length === 1 && rotuloFeriado(selected.itens[0].feriado) && (
              <Chip size="small" variant="outlined" label={rotuloFeriado(selected.itens[0].feriado)} />
            )}
          </Stack>
          {!selected || selected.itens.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Sem marca neste dia.</Typography>
          ) : (
            <Stack spacing={1.5}>
              {selected.itens.map((item) => (
                <DiaDetalhe
                  key={item.id}
                  item={item}
                  mode={mode}
                  onOpenJornada={onOpenJornada}
                  jornadaNoItem={selected.itens.length > 1}
                  ocultarChips={selected.itens.length === 1}
                />
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Stack>
  );
};

CalendarioJornada.propTypes = {
  mode: PropTypes.oneOf(['inspetor', 'geral']).isRequired,
  dias: PropTypes.arrayOf(PropTypes.object).isRequired,
  inicio: PropTypes.number.isRequired,
  fim: PropTypes.number.isRequired,
  onOpenJornada: PropTypes.func.isRequired,
  resumo: PropTypes.object,
  loading: PropTypes.bool,
  loadError: PropTypes.bool,
};

export default CalendarioJornada;
